export const pagesKey = ['pages'];
export const sellerPagesKey = (sellerId = null) => ['pages', sellerId];
export const sellerPagesKeyWithFilter = (sellerId = null, filter = null) =>
  filter ? ['pages', sellerId, filter] : ['pages', sellerId];

export const pageKey = pageId => ['pages', pageId];
// Under pageKey, so whatever refreshes the page refreshes its sidebar counts too.
export const pageCountsKey = pageId => [...pageKey(pageId), 'counts'];
export const pageSummaryKey = (pageId, range) => [...pageKey(pageId), 'summary', range];
// Under pageKey: a purchase or an AI change that refreshes the page refreshes this too.
export const aiPageResetKey = pageId => [...pageKey(pageId), 'ai-reset'];
// Under pageKey, so the preview refetches with the page.
export const guestQuestionsKey = pageId => [...pageKey(pageId), 'guest-questions'];
export const pageCheckoutsKey = filter => ['pages', 'checkouts', filter];
export const pageFormsKey = filter => ['pages', 'forms', filter];
export const pageTicketTypesKey = pageId => ['pages', 'ticket-types', pageId];
export const pageOrderBumpsKey = pageId => ['pages', pageId, 'order-bumps'];
export const pageOrderBumpInfoKey = (pageId, productId) => [
  ...pageOrderBumpsKey(pageId),
  productId,
  'info',
];
// Not under pageKey: every AI change invalidates the page, and that must not refetch the chat.
export const aiChatHistoryKey = (sellerId, pageId, userId) => [
  'ai-chat-history',
  sellerId,
  pageId,
  userId,
];

export const templatesKey = ['templates'];
export const templateKey = templateId => ['templates', templateId];

export const templateCategoriesKey = ['templates-categories'];
export const templateCategoryKey = templateCategoryId => [
  'templates-categories',
  templateCategoryId,
];

export const integrationsKey = ['integrations'];
export const integrationKey = integrationId => ['integrations', integrationId];
export const integrationsPageKey = pageId => ['integrations-page', pageId];
export const integrationEventsKey = integrationId => ['integration', integrationId, 'events'];

export const sellerProductsKey = (sellerId, search) => ['products', sellerId, search ?? ''];
// A product read on its own, outside the page payload that carries
// `page.product` — the order-bump forms edit a product no page owns.
export const productKey = productId => ['product', productId];

export const customerOriginKey = customerId => ['customers', customerId, 'origin'];
export const customerKey = customerId => ['customers', customerId];
export const customerHeldTicketsKey = customerId => ['customers', customerId, 'held-tickets'];

export const submissionKey = submissionId => ['submissions', submissionId];
export const submissionEmailLogsKey = submissionId => ['submissions', submissionId, 'email-logs'];

export const couponsKey = (sort, filter) => ['coupons', sort, filter];
export const couponKey = couponId => ['coupons', couponId];

export const themesKey = sellerId => ['themes', sellerId];
export const themeKey = (sellerId, themeId) => ['themes', sellerId, themeId];

export const sellerKey = sellerId => ['sellers', sellerId];
export const sellerUsageKey = sellerId => ['seller-usage', sellerId];
export const sellerBrandCacheKey = sellerId => ['brand', 'cache', sellerId];
export const adminSellerKey = sellerId => ['admin-sellers', sellerId];
export const eventDashboardOverviewKey = eventId => ['event-dashboard-overview', eventId];
export const checkoutOverviewKey = pageId => ['checkout-overview', pageId];
export const sellerBillingAddress = sellerId => ['seller-billing-address', sellerId];
export const sellerPaymentMethodKey = sellerId => ['seller-payment-method', sellerId];
export const sellerPlansKey = sellerId => ['seller-plans', sellerId];
export const sellerGoogleKey = sellerId => ['seller-oauthGoogleAccounts', sellerId];
export const sellerSlackKey = sellerId => ['seller-oauthSlackAccounts', sellerId];
export const sellerStripePaymentMethodConfigurationsKey = sellerId => [
  'seller',
  sellerId,
  'stripe',
  'paymentMethodConfigurations',
];

export const chargeKey = chargeId => ['charges', chargeId];
export const chargeInvoicesKey = chargeId => ['charges', chargeId, 'invoices'];
export const chargeEmailLogsKey = chargeId => ['charges', chargeId, 'email-logs'];
export const chargeTicketsKey = chargeId => ['charges', chargeId, 'tickets'];
export const chargeGuestsKey = chargeId => ['charges', chargeId, 'guests'];
export const attendeeKey = ticketId => ['attendees', ticketId];
export const chargeTransactionsKey = chargeId => ['charges', chargeId, 'transactions'];

export const subscriptionKey = subscriptionId => ['subscriptions', subscriptionId];
export const subscriptionPaymentsKey = subscriptionPaymentId => [
  'subscriptionPayments',
  subscriptionPaymentId,
];
export const subscriptionEmailLogsKey = subscriptionId => [
  'subscriptions',
  subscriptionId,
  'email-logs',
];

export const statisticKey = ({ metric, sellerId, dateStart, dateEnd, unit, timezoneOffset }) => [
  'statistics',
  metric,
  sellerId,
  dateStart,
  dateEnd,
  unit,
  timezoneOffset,
];

export const recentCustomersKey = sellerId => ['seller-recent-customers', sellerId];
export const recentPaymentsKey = sellerId => ['seller-recent-payments', sellerId];
export const recentBookingsKey = sellerId => ['seller-recent-bookings', sellerId];
export const recentSubscriptionsKey = sellerId => ['seller-recent-subscriptions', sellerId];
export const recentSubmissionsKey = sellerId => ['seller-recent-submissions', sellerId];

export const domainStatusKey = domain => ['domain-status', domain];

export const teamMembersKey = ['team-members'];
export const teamInvitesKey = ['team-invites'];

export const apiKeysKey = sellerId => ['apiKeys', sellerId];

export const webhooksKey = (sellerId, filters = {}) => ['webhooks', sellerId, filters];

export const blockedCustomersKey = sellerId => ['blocked-customers', sellerId];
export const linkInBioKey = sellerId => ['link-in-bio', sellerId];
export const linkInBioPagesKey = (sellerId, ids) => ['link-in-bio', sellerId, 'pages', ids];
export const linkInBioAnalyticsKey = (sellerId, query) => [
  'link-in-bio',
  sellerId,
  'analytics',
  query,
];

// Admin query keys
export const adminMerchantsKey = (filters = {}) => ['admin', 'merchants', filters];
export const adminMerchantKey = sellerId => ['admin', 'merchants', sellerId];
export const adminMerchantBillingHistoryKey = sellerId => [
  ...adminMerchantKey(sellerId),
  'billing-history',
];
export const adminPagesKey = (filters = {}) => ['admin', 'pages', filters];
export const adminPaymentsKey = (filters = {}) => ['admin', 'payments', filters];
export const adminStuckFreeChargeKey = chargeId => ['admin', 'stuck-free-charge', chargeId];
export const adminBulkBookingImportsKey = () => ['admin', 'bulk-booking-imports'];
export const adminSubmissionsKey = (filters = {}) => ['admin', 'submissions', filters];
export const adminSubscriptionsKey = (filters = {}) => ['admin', 'subscriptions', filters];
export const adminTemplatesKey = (filters = {}) => ['admin', 'templates', filters];
export const adminTemplateKey = templateId => ['admin', 'templates', templateId];
export const adminTemplateCategoriesKey = (filters = {}) => [
  'admin',
  'template-categories',
  filters,
];
export const adminTemplateCategoryKey = categoryId => ['admin', 'template-categories', categoryId];
export const adminIntegrationLogsKey = (filters = {}) => ['admin', 'integration-logs', filters];
export const adminStatisticLogsKey = (filters = {}) => ['admin', 'statistic-logs', filters];
export const adminMetricLogsKey = (filters = {}) => ['admin', 'metric-logs', filters];
export const adminPlansKey = () => ['admin', 'plans'];
export const adminActionsKey = (filters = {}) => ['admin', 'actions', filters];
export const adminHelpCenterSearchIntentsKey = (filters = {}) => [
  'admin',
  'help-center-search-intents',
  filters,
];
export const adminSellersKey = (filters = {}) => ['admin', 'sellers', filters];
export const adminUsersKey = (filters = {}) => ['admin', 'users', filters];
export const adminMerchantsUsageKey = (filters = {}) => ['admin', 'merchants-usage', filters];
export const adminPageCompareKey = (filters = {}) => ['admin', 'page-compare', filters];
export const adminPagePromptsKey = (filters = {}) => ['admin', 'page-prompts', filters];
export const adminThemesKey = (filters = {}) => ['admin', 'themes', filters];
export const adminApiKeysKey = (filters = {}) => ['admin', 'api-keys', filters];
export const adminOAuthTokensKey = (filters = {}) => ['admin', 'oauth-tokens', filters];
export const adminWebhooksKey = (filters = {}) => ['admin', 'webhooks', filters];
export const adminLinkInBiosKey = (filters = {}) => ['admin', 'link-in-bio', filters];
export const adminBrandLogKey = () => ['admin', 'brand-log'];
export const adminAiUsageKey = ['admin', 'ai-usage'];
export const adminAiUsageSummaryKey = (filters = {}) => [...adminAiUsageKey, 'summary', filters];
export const adminAiUsageStoresKey = (filters = {}) => [...adminAiUsageKey, 'stores', filters];
export const adminAiUsageStoreKey = (sellerId, filters = {}) => [
  ...adminAiUsageKey,
  'store',
  sellerId,
  filters,
];
export const adminAiUsageTurnsKey = (filters = {}) => [...adminAiUsageKey, 'turns', filters];
export const adminPageAiStatusKey = pageId => [...adminAiUsageKey, 'page', pageId];
export const adminEmailTemplatesCatalogKey = () => ['admin', 'email-templates', 'catalog'];
export const adminEmailTemplateRenderKey = previewId => [
  'admin',
  'email-templates',
  'render',
  previewId,
];
export const adminEmailTemplatePagesKey = (q, includeTests) => [
  'admin',
  'email-templates',
  'pages',
  { q, includeTests },
];
export const adminEmailTemplateLivePageKey = (pageId, priceId) => [
  'admin',
  'email-templates',
  'live-page',
  pageId,
  priceId,
];
export const adminEmailTemplateLiveEmailKey = path => [
  'admin',
  'email-templates',
  'live-email',
  path,
];

export const sellerTaxRatesKey = sellerId => ['seller-tax-rates', sellerId];

export const setupPageCountKey = sellerId => ['setup', 'pageCount', sellerId];
export const setupCouponExistsKey = sellerId => ['setup', 'couponExists', sellerId];

export const paletteSearchKey = (groupKey, sellerId, query) => [
  'commandPalette',
  groupKey,
  sellerId,
  query,
];

export const bookingCouponOptionsKey = (sellerId, eventId, currency, search = '') => [
  'coupons',
  'booking-options',
  sellerId,
  eventId,
  currency,
  search,
];
export const sellerBookingsKey = sellerId => ['charges', sellerId];

export const recentChangelogEntriesKey = ['changelog', 'recent'];
